import { Dispatch, SetStateAction, useContext, useState } from "react";
import { Modal, Button, SpinnerCircular, Switchbox, WeirDatePicker } from "@weirgroup/weir-dls";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { AppConfigContext } from "../../../contexts/app-config.context";
import { FetchedUserSettingType, UserSettingType } from "../../../types/user.type";
import userSettingsApi from "../../../api/settings.api";
import { processResult } from "../../../utils/process-result.util";
import { SetFunctionType } from "../../../types/utils.type";

import "../styles.scss";
interface Props {
    isSettingsModalShown: boolean
    setIsSettingsModalShown: Dispatch<SetStateAction<boolean>>;
    startDate: string;
    endDate: string;
    setStartDate: Dispatch<SetStateAction<any>>;
    setEndDate: Dispatch<SetStateAction<any>>;
    fetchNotifications: () => void;
    setToastData: Dispatch<SetStateAction<any>>;
    setShowToast: Dispatch<SetStateAction<any>>;
    userSettings: FetchedUserSettingType;
    fetchNotificationConfigs: () => void;
    darkMode?:boolean;
}

export const SettingsModal = (props: Props) => {
    const {
        isSettingsModalShown,
        setIsSettingsModalShown,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        fetchNotifications,
        setToastData,
        setShowToast,
        userSettings,
        fetchNotificationConfigs,
        darkMode
    } = props;
    const appConfigStore = useContext(AppConfigContext);
    const [geofenceEmailNotification, setGeofenceEmailNotification] = useState<boolean>(userSettings.geofence_email_notification);
    const [assetEmailNotification, setAssetEmailNotification] = useState<boolean>(userSettings.asset_email_notification);
    const [inAppNotification, setInAppNotification] = useState<boolean>(userSettings.inapp_notification);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const setFunctions: SetFunctionType = {
        setIsLoading,
        setIsModalShown: setIsSettingsModalShown,
        setToastData,
        setShowToast
    }
    const { t } = useTranslation();

    const setDates = (dates: string[]) => {
        setStartDate(dayjs(dates[0]).format('l'));
        setEndDate(dayjs(dates[1]).format('l'));
    }

    const saveConfigs = () => {
        setIsLoading(true)
        const userSettings: UserSettingType = {
            language: appConfigStore.language,
            timezone: appConfigStore.timezone,
            units: appConfigStore.units,
            asset_email_notification: assetEmailNotification,
            geofence_email_notification: geofenceEmailNotification,
            inapp_notification: inAppNotification
        }
        userSettingsApi.updateUserSettings(userSettings).then(() => {
            processResult(setFunctions, t('saveNotificationConfigMessage'), true)
            fetchNotifications();
            fetchNotificationConfigs();
        }).catch((err: any) => {
            const message = err.response.data.message ?? t('failNotificationConfigMessage')
            processResult(setFunctions, message, false)
            fetchNotifications();
            fetchNotificationConfigs();
        })
    }
    return (
        <div className="settings-config">
            <Modal
                darkMode={darkMode}
                showModal={isSettingsModalShown}
                onClose={() => setIsSettingsModalShown(false)}
                size="large"
                canvasDeviceSize="tablet"
                dismissable={true}
            >
                <div className="content">
                    <p className="title" style={{color:darkMode ? "white": "black"}}>{t('notificationSettings')}</p>
                    <div className="toggle-container">
                        <p className="tooltip" style={{color:darkMode ? "white": "gray"}}>{t("toggleTooltip")}</p>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={(e: any) => setAssetEmailNotification(!assetEmailNotification)}
                                checked={assetEmailNotification}
                                label={t("assetEmailNotifications")}
                            />
                        </div>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={(e: any) => setGeofenceEmailNotification(!geofenceEmailNotification)}
                                checked={geofenceEmailNotification}
                                label={t("geofenceEmailNotifications")}
                            />
                        </div>
                        <div className="toggles">
                            <Switchbox
                                darkMode={darkMode}
                                onChange={(e: any) => setInAppNotification(!inAppNotification)}
                                checked={inAppNotification}
                                label={t("inAppNotifications")}
                            />
                        </div>
                    </div>
                    <div className="date-range">
                        <p className="tooltip" style={{color:darkMode ? "white": "gray"}}>{t("dateRangeToolTip")}</p>
                        <WeirDatePicker
                            darkMode={darkMode}
                            label={t("notificationDateRange")}
                            size={"md"}
                            ranged={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(e: any) => setDates(e)}
                        />
                    </div>
                </div>
                <div className="save-btn">
                    <Button
                        darkMode={darkMode}
                        label={t("saveSettings")}
                        size={"sm"}
                        isLoading={isLoading}
                        onClick={() => saveConfigs()}
                    />
                </div>
                {isLoading ?
                    <div className="spinner">
                        <SpinnerCircular  darkMode={darkMode} />
                        <p className="spinner-label" style={{color:darkMode ? "white" : "black"}}>{t('loading')}</p>
                    </div> : <></>
                }
            </Modal>
        </div>
    )
}